import { useCallback, useEffect, useState, useMemo } from 'react';

import { addDays, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';

import { Button, Typography, Spinner, Space, Modal } from '@ag1/ui';
import usePageId from 'shared/hooks/usePageId';
import { showNotification } from 'shared/ui/Notification';
import { useDate } from 'shared/hooks';
import { useChangeOrderDateV2 } from 'state/hooks/useChangeOrderDateV2';
import { getDurationForSubscription } from 'shared/utils/getDurationForSubscription';
import { useQueryGetSubscriptionDetailsV2 } from 'state/internal/getSubscriptionDetails';

import styles from './SelectDateChange.module.scss';
import { CHANGE_ORDER_DATE_STEP } from '../../constants';
import { DeliveryFrequencyInfo } from '../../../DeliveryFrequencyV2/components/DeliveryFrequencyInfo';
import { SelectDatepickerV2 } from '../SelectDatepickerV2';
import useSelectOrderDateModalV2 from '../../hooks/useSelectOrderDateModalV2';

const period = 'day';

type SelectDateChangeModalV2Props = {
  referenceId?: number;
  intervalFrequency: number;
  onClose?: () => void;
};

const SelectDateChangeModalV2 = ({
  referenceId,
  intervalFrequency,
  onClose
}: SelectDateChangeModalV2Props) => {
  const { t } = useTranslation('modalWindows');

  const pageId = usePageId();
  const contractId = referenceId || pageId;

  const { data: subscriptionData } = useQueryGetSubscriptionDetailsV2(contractId);
  const { setStep, info, setInfo } = useSelectOrderDateModalV2();
  const { onChangeOrderDate, isPending, isSuccess } = useChangeOrderDateV2();

  const { date: nextOrderDate } = useDate({
    date: subscriptionData?.next_order_date,
    fallbackDate: addDays(new Date(), 1)
  });
  const { formattedDateString: updatedNextOrderDate } = useDate({
    date: info?.date,
    dateFormat: 'MMMM d, yyyy'
  });

  const durationForSubscription = getDurationForSubscription(intervalFrequency, period) || null;

  const [newDateValue, setNewDateValue] = useState<Date>(nextOrderDate);
  const isSameNextDate = isSameDay(newDateValue, nextOrderDate);

  const handleDate = (newDate?: Date) => {
    if (newDate) {
      setNewDateValue(newDate);
    }
  };

  const handleSubmit = useCallback(() => {
    if (newDateValue) {
      onChangeOrderDate({
        contractId,
        oldOrderDate: nextOrderDate,
        newOrderDate: newDateValue
      });
      setInfo({
        date: newDateValue,
        duration: durationForSubscription
      });
    } else {
      showNotification('mutationError');
    }
  }, [
    durationForSubscription,
    nextOrderDate,
    newDateValue,
    onChangeOrderDate,
    setInfo,
    contractId
  ]);

  useEffect(() => {
    if (isSuccess) {
      if (referenceId) {
        onClose?.();
        showNotification('mutationSuccess', {
          title: t('changeOrderDate.newDateSuccessText', { date: updatedNextOrderDate })
        });
      } else {
        setStep(CHANGE_ORDER_DATE_STEP.success);
      }
    }
  }, [t, referenceId, isSuccess, onClose, setStep, updatedNextOrderDate]);

  const dateAsNumber = useMemo(() => Math.floor(newDateValue.getTime() / 1000), [newDateValue]);

  return (
    <>
      <Modal.Header>{t('changeOrderDate.title')}</Modal.Header>
      <Modal.Content hasFooter>
        <Typography variant="portalText" size="1" color="black-new" className={styles.title}>
          {t('changeOrderDate.description')}
        </Typography>
        <SelectDatepickerV2
          value={newDateValue}
          defaultValue={nextOrderDate}
          onChange={(value) => handleDate(value)}
          nextOrderDate={nextOrderDate}
        />
        <DeliveryFrequencyInfo newDateValue={dateAsNumber} option={intervalFrequency.toString()} />
      </Modal.Content>
      <Modal.Footer>
        <Space right={{ s: 100, xxs: 0 }} left={{ s: 100, xxs: 0 }} className={styles.button}>
          <Button
            fluid
            theme="new"
            type="submit"
            size={{ xxs: 'small', s: 'medium' }}
            variant="primary"
            disabled={isPending || isSameNextDate}
            onClick={handleSubmit}>
            {isPending ? (
              <Spinner color="brand-white" size={24} withOverlay={false} />
            ) : (
              t('changeOrderDate.confirmButton')
            )}
          </Button>
        </Space>
      </Modal.Footer>
    </>
  );
};

export default SelectDateChangeModalV2;
